import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Rating } from "@mui/material";
import "./WriteReviewComponent.scss";
import { Close, StarRounded } from "@mui/icons-material";
import { submitReview } from "../../../api/details-page.api";

interface WriteReviewComponentProps {
  onClose: () => void;
  partnerId: number;
  onSubmitSuccess: () => void;
  onSubmitFailed: () => void;
}

const WriteReviewComponent: React.FC<WriteReviewComponentProps> = ({
  onClose,
  partnerId,
  onSubmitSuccess,
  onSubmitFailed,
}) => {
  const [rating, setRating] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [ratingError, setRatingError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const onWriteClose = () => {
    setRating("");
    setDescription("");
    onClose();
  };
  useEffect(() => {
    setRating("");
    setDescription("");
  }, []);

  const handleSubmit = async () => {
    let hasError = false;
    if (!rating) {
      setRatingError("Rating is required.");
      hasError = true;
    } else {
      setRatingError("");
    }
    if (!description.trim()) {
      setDescriptionError("Description is required.");
      hasError = true;
    } else {
      setDescriptionError("");
    }

    if (hasError) {
      return;
    }

    try {
      const response = await submitReview({
        partnerId,
        description,
        rating: Number(rating),
      });
      if (response?.status === 200 && response?.data?.success) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        onSubmitSuccess();
        onWriteClose();
      } else {
        onSubmitFailed();
      }
     
    } catch (err) {
      setSubmitError("Failed to submit review. Please try again.");
      console.error(err);
      onSubmitFailed();
    }
  };

  return (
    <Box className="write-review-card">
      <Box className="write-review-header">
        <Typography className="write-review-title">
          Share Your Experience
        </Typography>
        <Typography className="close-btn" onClick={onWriteClose}>
          <Close
            fontSize="small"
            sx={{
              color: "rgba(156, 153, 145, 1)",
            }}
          />
        </Typography>
      </Box>
      <Box className="write-review-body">
        <Box className="rating-section">
          <Typography className="rating-label">Rating *</Typography>
          <Rating
            name="rating"
            value={typeof rating === "number" ? rating : 0}
            onChange={(event, newValue) => {
              setRating(newValue || "");
              setRatingError("");
              setSubmitError("");
            }}
            size="large"
            icon={<StarRounded fontSize="inherit" />}
            emptyIcon={
              <StarRounded style={{ opacity: 0.55 }} fontSize="inherit" />
            }
          />
          {ratingError && <Box className="error-text">{ratingError}</Box>}
        </Box>
        <Box
          className="review-section"
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderWidth: "1px",
                borderColor: "rgba(0, 0, 0, 0.23)",
              },
            "& .MuiFormHelperText-root": {
              margin: 0,
              padding: 0,
              lineHeight: "16px",
            },
          }}
        >
          <Typography className="review-label">Your Review *</Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            placeholder="Share your experience..."
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDescriptionError("");
              setSubmitError("");
            }}
            fullWidth
            error={!!descriptionError}
            helperText={descriptionError}
          />
        </Box>
        {submitError && <Box className="submit-error-text">{submitError}</Box>}
      </Box>
      <Box className="write-review-actions">
        <Button className="submit-review-btn" onClick={handleSubmit}>
          Submit Review
        </Button>
        <Button className="cancel-btn" onClick={onWriteClose}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default WriteReviewComponent;
